// @ts-strict-ignore
import { useMutation, useQuery } from "@apollo/client";
import ActionDialog from "@dashboard/components/ActionDialog";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import TableCellHeader from "@dashboard/components/TableCellHeader";
import useNotifier from "@dashboard/hooks/useNotifier";
import {
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Tab,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import ReplayIcon from "@material-ui/icons/Replay";
import SendIcon from "@material-ui/icons/Send";
import SyncIcon from "@material-ui/icons/Sync";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { Box, Button, Input, SearchInput, Skeleton, Text } from "@saleor/macaw-ui-next";
import React, { useState } from "react";

import FilterTabs, { FilterTab } from "@dashboard/components/TableFilter";
import CampaignCreateWizard from "../components/CampaignCreateWizard";
import MediaLibraryTab from "../components/MediaLibraryTab";
import TemplateDialog from "../components/TemplateDialog";
import TemplatePreviewDialog from "../components/TemplatePreviewDialog";
import {
  WHATSAPP_CAMPAIGN_DELETE_MUTATION,
  WHATSAPP_CAMPAIGN_DUPLICATE_MUTATION,
  WHATSAPP_CAMPAIGN_SEND_MUTATION,
  WHATSAPP_TEMPLATE_DELETE_MUTATION,
  WHATSAPP_TEMPLATES_SYNC_MUTATION,
} from "../waMutations";
import {
  WHATSAPP_CAMPAIGN_DETAILS_QUERY,
  WHATSAPP_CAMPAIGNS_QUERY,
  WHATSAPP_TEMPLATES_QUERY,
} from "../waQueries";
import { WhatsAppCampaign, WhatsAppTemplate } from "../types";

interface DeleteTarget {
  id: string;
  name: string;
  type: "WhatsApp Template" | "WhatsApp Campaign Broadcast";
}

export const WhatsAppPage: React.FC = () => {
  const notify = useNotifier();
  const [tabIndex, setTabIndex] = useState<number>(0);

  // Dialog states
  const [wizardOpen, setWizardOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<WhatsAppTemplate | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<WhatsAppTemplate | null>(null);

  // Delete Confirmation state
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  // Duplicate Campaign state
  const [duplicateTarget, setDuplicateTarget] = useState<{ id: string; name: string } | null>(null);
  const [duplicateName, setDuplicateName] = useState("");

  // Recipient details audit modal
  const [inspectCampaignId, setInspectCampaignId] = useState<string | null>(null);

  // Queries
  const { data: campaignsData, loading: campaignsLoading, refetch: refetchCampaigns } = useQuery(
    WHATSAPP_CAMPAIGNS_QUERY,
    { pollInterval: 10000 } // Live polling every 10s for active progress updates
  );
  const { data: templatesData, loading: templatesLoading } = useQuery(WHATSAPP_TEMPLATES_QUERY);
  const { data: inspectData, loading: inspectLoading } = useQuery(WHATSAPP_CAMPAIGN_DETAILS_QUERY, {
    variables: { id: inspectCampaignId || "" },
    skip: !inspectCampaignId,
  });

  // Mutations
  const [sendCampaign, { loading: sending }] = useMutation(WHATSAPP_CAMPAIGN_SEND_MUTATION, {
    onCompleted: data => {
      if (data.whatsappCampaignSend?.errors?.length) {
        notify({ status: "error", text: data.whatsappCampaignSend.errors[0].message });
      } else {
        notify({ status: "success", text: "Campaign dispatched to background workers" });
        refetchCampaigns();
      }
    },
    onError: err => {
      notify({ status: "error", text: err.message });
    },
  });

  const [deleteTemplate, { loading: deletingTemplate }] = useMutation(WHATSAPP_TEMPLATE_DELETE_MUTATION, {
    refetchQueries: [{ query: WHATSAPP_TEMPLATES_QUERY }],
    onCompleted: () => {
      notify({ status: "success", text: "Template removed" });
      setDeleteTarget(null);
    },
    onError: err => {
      notify({ status: "error", text: err.message });
    },
  });

  const [deleteCampaign, { loading: deletingCampaign }] = useMutation(WHATSAPP_CAMPAIGN_DELETE_MUTATION, {
    refetchQueries: [{ query: WHATSAPP_CAMPAIGNS_QUERY }],
    onCompleted: () => {
      notify({ status: "success", text: "Campaign broadcast removed" });
      setDeleteTarget(null);
    },
    onError: err => {
      notify({ status: "error", text: err.message });
    },
  });

  const [syncTemplates, { loading: syncing }] = useMutation(WHATSAPP_TEMPLATES_SYNC_MUTATION, {
    refetchQueries: [{ query: WHATSAPP_TEMPLATES_QUERY }],
    onCompleted: data => {
      const errors = data.whatsappTemplatesSync?.errors;
      if (errors?.length) {
        notify({ status: "error", text: errors[0].message });
      } else {
        const count = data.whatsappTemplatesSync?.syncedCount ?? 0;
        notify({
          status: "success",
          text: `Successfully synced ${count} approved templates from Meta WhatsApp!`,
        });
      }
    },
    onError: err => {
      notify({ status: "error", text: err.message });
    },
  });

  const [duplicateCampaign, { loading: duplicating }] = useMutation(WHATSAPP_CAMPAIGN_DUPLICATE_MUTATION, {
    refetchQueries: [{ query: WHATSAPP_CAMPAIGNS_QUERY }],
    onCompleted: data => {
      const errors = data.whatsappCampaignDuplicate?.errors;
      if (errors?.length) {
        notify({ status: "error", text: errors[0].message });
      } else {
        const copyName = data.whatsappCampaignDuplicate?.campaign?.name;
        notify({ status: "success", text: `Campaign cloned as "${copyName}" in Draft` });
        setDuplicateTarget(null);
        refetchCampaigns();
      }
    },
    onError: err => {
      notify({ status: "error", text: err.message });
    },
  });

  // Template Search and Category Filtering States
  const [templateSearch, setTemplateSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("ALL");

  const allTemplates: WhatsAppTemplate[] = templatesData?.whatsappTemplates || [];
  const marketingCount = allTemplates.filter(t => t.category === "MARKETING").length;
  const utilityCount = allTemplates.filter(t => t.category === "UTILITY").length;
  const authCount = allTemplates.filter(t => t.category === "AUTHENTICATION").length;

  const filteredTemplates = allTemplates.filter(t => {
    const matchesCategory = selectedCategory === "ALL" || t.category === selectedCategory;
    const query = templateSearch.toLowerCase().trim();
    const matchesSearch =
      !query ||
      t.displayName.toLowerCase().includes(query) ||
      t.name.toLowerCase().includes(query);
    return matchesCategory && matchesSearch;
  });

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    if (deleteTarget.type === "WhatsApp Campaign Broadcast") {
      deleteCampaign({ variables: { id: deleteTarget.id } });
    } else {
      deleteTemplate({ variables: { id: deleteTarget.id } });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "COMPLETED":
        return <Chip size="small" label="Completed" style={{ backgroundColor: "#dcfce7", color: "#15803d" }} />;
      case "SENDING":
        return <Chip size="small" label="Sending..." style={{ backgroundColor: "#e0f2fe", color: "#0369a1" }} />;
      case "PARTIALLY_COMPLETED":
        return <Chip size="small" label="Partial Success" style={{ backgroundColor: "#fef3c7", color: "#b45309" }} />;
      case "FAILED":
        return <Chip size="small" label="Failed" style={{ backgroundColor: "#fee2e2", color: "#b91c1c" }} />;
      default:
        return <Chip size="small" label="Draft" style={{ backgroundColor: "#f1f5f9", color: "#475569" }} />;
    }
  };

  return (
    <ListPageLayout>
      <TopNav title="WhatsApp Broadcast & Campaign Messaging">
        {tabIndex === 0 && (
          <Button onClick={() => setWizardOpen(true)}>Create Campaign</Button>
        )}
        {tabIndex === 1 && (
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              variant="secondary"
              onClick={() => syncTemplates()}
              disabled={syncing}
            >
              {syncing ? (
                <CircularProgress size={14} style={{ marginRight: 6 }} />
              ) : (
                <SyncIcon fontSize="small" style={{ marginRight: 6 }} />
              )}
              {syncing ? "Syncing..." : "Sync from Meta"}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setEditingTemplate(null);
                setTemplateDialogOpen(true);
              }}
            >
              Register Template
            </Button>
          </Box>
        )}
      </TopNav>

      <Box display="flex" flexDirection="column" gap={4}>
        <DashboardCard>
          <Tabs
            value={tabIndex}
            onChange={(_, val) => setTabIndex(val)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Campaign Broadcasts" />
            <Tab label="Registered Meta Templates" />
            <Tab label="Media Library (Bucket Storage)" />
          </Tabs>
        </DashboardCard>

        {/* ── TAB 0: CAMPAIGNS ── */}
        {tabIndex === 0 && (
          <DashboardCard>
            {campaignsLoading ? (
              <Box padding={4}><Skeleton /></Box>
            ) : (
              <ResponsiveTable>
                <TableHead>
                  <TableRow>
                    <TableCellHeader>Campaign Name</TableCellHeader>
                    <TableCellHeader>Template</TableCellHeader>
                    <TableCellHeader>Recipients</TableCellHeader>
                    <TableCellHeader>Sent / Failed</TableCellHeader>
                    <TableCellHeader>Status</TableCellHeader>
                    <TableCellHeader>Created At</TableCellHeader>
                    <TableCellHeader textAlign="right" style={{ width: 180 }}>Actions</TableCellHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(campaignsData?.whatsappCampaigns || []).map((c: WhatsAppCampaign) => (
                    <TableRow key={c.id} hover>
                      <TableCell><Typography variant="subtitle2">{c.name}</Typography></TableCell>
                      <TableCell>{c.template.displayName} ({c.template.name})</TableCell>
                      <TableCell>{c.totalRecipients}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Text size={2} style={{ color: "#16a34a", fontWeight: 600 }}>{c.sentCount} sent</Text>
                          {c.failedCount > 0 && (
                            <Text size={2} style={{ color: "#dc2626", fontWeight: 600 }}>/ {c.failedCount} failed</Text>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{getStatusBadge(c.status)}</TableCell>
                      <TableCell>{new Date(c.createdAt).toLocaleDateString()}</TableCell>
                      <TableCell align="right" style={{ width: 180 }}>
                        <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                          <IconButton
                            size="small"
                            title="Inspect Recipients & Delivery Logs"
                            onClick={() => setInspectCampaignId(c.id)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          {c.status === "DRAFT" && (
                            <IconButton
                              size="small"
                              title="Send Campaign Now"
                              style={{ color: "#2563eb" }}
                              onClick={() => sendCampaign({ variables: { id: c.id } })}
                              disabled={sending}
                            >
                              <SendIcon fontSize="small" />
                            </IconButton>
                          )}
                          {c.failedCount > 0 && (
                            <IconButton
                              size="small"
                              title={`Retry Failed Recipients (${c.failedCount})`}
                              style={{ color: "#dc2626" }}
                              onClick={() => sendCampaign({ variables: { id: c.id } })}
                              disabled={sending}
                            >
                              <ReplayIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            title="Duplicate Campaign (Clone as New Draft)"
                            style={{ color: "#4f46e5" }}
                            onClick={() => {
                              setDuplicateTarget({ id: c.id, name: c.name });
                              setDuplicateName(`${c.name} (Copy)`);
                            }}
                            disabled={duplicating}
                          >
                            <FileCopyIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            title="Delete Campaign"
                            onClick={() =>
                              setDeleteTarget({
                                id: c.id,
                                name: c.name,
                                type: "WhatsApp Campaign Broadcast",
                              })
                            }
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!campaignsData?.whatsappCampaigns || campaignsData.whatsappCampaigns.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Box padding={4}><Text>No campaigns created yet.</Text></Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </ResponsiveTable>
            )}
          </DashboardCard>
        )}

        {/* ── TAB 1: TEMPLATES ── */}
        {tabIndex === 1 && (
          <DashboardCard>
            {/* Standard Saleor Category Filter Tabs */}
            <FilterTabs
              currentTab={
                selectedCategory === "MARKETING"
                  ? 1
                  : selectedCategory === "UTILITY"
                  ? 2
                  : selectedCategory === "AUTHENTICATION"
                  ? 3
                  : 0
              }
            >
              <FilterTab
                label={`All (${allTemplates.length})`}
                onClick={() => setSelectedCategory("ALL")}
              />
              <FilterTab
                label={`Marketing (${marketingCount})`}
                onClick={() => setSelectedCategory("MARKETING")}
              />
              <FilterTab
                label={`Utility (${utilityCount})`}
                onClick={() => setSelectedCategory("UTILITY")}
              />
              <FilterTab
                label={`Authentication (${authCount})`}
                onClick={() => setSelectedCategory("AUTHENTICATION")}
              />
            </FilterTabs>

            {/* Standard Saleor Search Bar */}
            <Box paddingX={6} paddingY={3} display="flex" alignItems="center">
              <Box style={{ width: "100%", maxWidth: 380 }}>
                <SearchInput
                  placeholder="Search templates by title or name..."
                  value={templateSearch}
                  onChange={e => setTemplateSearch(e.target.value)}
                  size="small"
                />
              </Box>
            </Box>

            {templatesLoading ? (
              <Box padding={4}><Skeleton /></Box>
            ) : (
              <ResponsiveTable>
                <TableHead>
                  <TableRow>
                    <TableCellHeader>Display Title</TableCellHeader>
                    <TableCellHeader>Meta Name</TableCellHeader>
                    <TableCellHeader>Category</TableCellHeader>
                    <TableCellHeader>Language</TableCellHeader>
                    <TableCellHeader>Campaigns</TableCellHeader>
                    <TableCellHeader>Status</TableCellHeader>
                    <TableCellHeader textAlign="right" style={{ width: 140 }}>Actions</TableCellHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTemplates.map((t: WhatsAppTemplate) => (
                    <TableRow key={t.id} hover>
                      <TableCell><Typography variant="subtitle2">{t.displayName}</Typography></TableCell>
                      <TableCell><Typography variant="body2" style={{ fontFamily: "monospace" }}>{t.name}</Typography></TableCell>
                      <TableCell><Chip size="small" label={t.category} /></TableCell>
                      <TableCell>{t.languageCode}</TableCell>
                      <TableCell>{t.campaignsCount}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={t.isActive ? "Approved" : "Disabled"}
                          style={{
                            backgroundColor: t.isActive ? "#dcfce7" : "#fee2e2",
                            color: t.isActive ? "#15803d" : "#b91c1c",
                          }}
                        />
                      </TableCell>
                      <TableCell align="right" style={{ width: 140 }}>
                        <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                          <IconButton
                            size="small"
                            onClick={() => setPreviewTemplate(t)}
                            title="Preview WhatsApp Template"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditingTemplate(t);
                              setTemplateDialogOpen(true);
                            }}
                            title="Edit Template"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setDeleteTarget({
                                id: t.id,
                                name: `${t.displayName} (${t.name})`,
                                type: "WhatsApp Template",
                              })
                            }
                            title="Delete Template"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredTemplates.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Box padding={4}>
                          <Text>No templates match the selected category or search filter.</Text>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </ResponsiveTable>
            )}
          </DashboardCard>
        )}

        {/* ── TAB 2: MEDIA LIBRARY ── */}
        {tabIndex === 2 && <MediaLibraryTab />}
      </Box>


      {/* Recipient Details Audit Modal */}
      <Dialog
        open={!!inspectCampaignId}
        onClose={() => setInspectCampaignId(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Campaign Delivery Audit Log
            </Typography>
            <IconButton
              size="small"
              onClick={() => setInspectCampaignId(null)}
              title="Close modal"
              style={{ color: "#64748b" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {inspectLoading ? (
            <Box padding={4}><LinearProgress /></Box>
          ) : (
            <Box display="flex" flexDirection="column" gap={3}>
              <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                {inspectData?.whatsappCampaign?.name} ({inspectData?.whatsappCampaign?.template?.displayName})
              </Typography>
              <ResponsiveTable>
                <TableHead>
                  <TableRow>
                    <TableCellHeader>Customer</TableCellHeader>
                    <TableCellHeader>Phone</TableCellHeader>
                    <TableCellHeader>Status</TableCellHeader>
                    <TableCellHeader>Message ID / Error</TableCellHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(inspectData?.whatsappCampaign?.recipients || []).map((r: any) => (
                    <TableRow key={r.id}>
                      <TableCell>{r.customer?.firstName} {r.customer?.lastName}</TableCell>
                      <TableCell style={{ fontFamily: "monospace" }}>{r.phoneNumber}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={r.status}
                          style={{
                            backgroundColor: r.status === "SENT" || r.status === "DELIVERED" ? "#dcfce7" : r.status === "FAILED" ? "#fee2e2" : "#f1f5f9",
                            color: r.status === "SENT" || r.status === "DELIVERED" ? "#15803d" : r.status === "FAILED" ? "#b91c1c" : "#475569",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {r.errorMessage ? (
                          <Typography variant="caption" color="error">{r.errorMessage}</Typography>
                        ) : (
                          <Typography variant="caption" style={{ fontFamily: "monospace" }}>{r.whatsappMessageId || "—"}</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </ResponsiveTable>
            </Box>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px" }}>
          <Button variant="secondary" onClick={() => setInspectCampaignId(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>


      {/* Create Wizard & Template Dialog */}
      <CampaignCreateWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
      <TemplateDialog
        key={editingTemplate?.id || "new-template"}
        open={templateDialogOpen}
        template={editingTemplate}
        onClose={() => {
          setTemplateDialogOpen(false);
          setEditingTemplate(null);
        }}
      />
      <TemplatePreviewDialog
        open={!!previewTemplate}
        template={previewTemplate}
        onClose={() => setPreviewTemplate(null)}
      />

      {/* Duplicate Campaign Confirmation Popup with Custom Name */}
      <ActionDialog
        open={!!duplicateTarget}
        title="Duplicate Campaign Broadcast"
        confirmButtonLabel="Duplicate & Create Draft"
        confirmButtonState={duplicating ? "loading" : "default"}
        onClose={() => setDuplicateTarget(null)}
        onConfirm={() => {
          if (!duplicateTarget) return;
          duplicateCampaign({
            variables: {
              id: duplicateTarget.id,
              name: duplicateName.trim() || undefined,
            },
          });
        }}
      >
        <Box display="flex" flexDirection="column" gap={3}>
          <Text>
            This will create a new <strong>Draft</strong> campaign with the identical message template, dynamic parameters, and recipient list from <strong>{duplicateTarget?.name}</strong>.
          </Text>
          <Input
            label="Campaign Name"
            value={duplicateName}
            onChange={e => setDuplicateName(e.target.value)}
            placeholder="Enter new campaign name..."
            width="100%"
            autoFocus
            helperText="You can customize the campaign name or keep the default copy name."
          />
        </Box>
      </ActionDialog>

      {/* Delete Confirmation Popup */}
      <ActionDialog
        open={!!deleteTarget}
        title={`Delete ${deleteTarget?.type}?`}
        variant="delete"
        confirmButtonLabel="Delete"
        confirmButtonState={deletingTemplate || deletingCampaign ? "loading" : "default"}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      >
        <Text>
          Are you sure you want to delete {deleteTarget?.type === "WhatsApp Campaign Broadcast" ? "campaign broadcast" : "template"}{" "}
          <strong>{deleteTarget?.name}</strong>? This action cannot be undone.
        </Text>
      </ActionDialog>
    </ListPageLayout>
  );
};

export default WhatsAppPage;
